import { apiErrorText } from "../../api/api-error.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Building"` — states `queued`, `running`, `succeeded`, `failed`.
 *
 * AMENDED 2026-09-25 (customer-portal review #28, #29, #30, #131, #32):
 *
 *   · THE ROW FIRST, THE STREAM ON TOP (#28). `GET /v1/stream/build/{id}` sends a new subscriber
 *     heartbeats, not the current state, so a reload, a second tab or a link opened later showed
 *     "0 % · queued" for a build that was running, had failed or was done. The screen now reads
 *     `GET /v1/builds/{id}` on load, again whenever a stream frame arrives, and every few seconds
 *     while the build is not finished — the rows are the truth and the stream is a view (design
 *     §16.7.2). All six steps are listed from the row, each with its state.
 *   · SIGNS OF LIFE (#30). The current step says how long it has been running ("1 min 12 s so
 *     far"). Active progress is indeterminate: stage ordinals do not measure work or time.
 *     Document checks are read from persisted rounds; no duration or percentage is invented.
 *   · A FAILED BUILD (#29, #32) says why, in the owner's words, and offers "Try again" (the same
 *     documents), "Replace document" (new ones) and "Open the agent" — never "Back to agents · we
 *     will notify you", and never "your existing agents keep answering" on a first agent.
 *   · A SUCCESSFUL BUILD (#131) leads to the new agent's page and names the next step — "Connect a
 *     number" when nothing reaches it yet — instead of a generic "Place a test call" into an empty
 *     Test console. No email is promised: none is sent. The page stays on its "ready" state rather
 *     than redirecting, so it remains a record of the build a person can come back to.
 *
 * AMENDED 2026-09-25 (customer-portal review 169, fix-round package Q): a successful build offers
 * "Test it", which opens the Test console ON THIS AGENT (`/test-console?agent=<id>`) — a link with no
 * agent opened the console on whichever agent came first in the list, so an owner could test (and
 * pay for) a different agent without noticing.
 *
 * `resync` is honoured: the server sends it instead of silently dropping events, and the screen
 * falls back on the row rather than leaving a progress bar frozen at a number that is no longer true.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@wetalk/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { readRecord, readText, readWholeNumber } from "../../api/decode.js";
import { QUERY_KEY, voiceAgentQuery } from "../../api/query.js";
import type { BuildJobView, DocumentCheckView } from "../../api/build-view.js";
import { lifecycleRefusalOf } from "../../api/slice/voice-agent-lifecycle.js";
import { meterStyle } from "../../meter.js";
import { recallBuildOrigin, rememberBuildOrigin } from "./build-origin.js";
import "../agents/screen.css";
import { useStagePresentation } from "./stage-presentation.js";
import "./building.css";

type BuildPhase = "queued" | "waiting" | "running" | "succeeded" | "failed";

interface StepRow {
  readonly ordinal: number;
  readonly label: string;
  readonly state: string;
  readonly detail: string | null;
  readonly startedAtMs: number | null;
}

/** How often the row is read again while the build is not finished, stream or no stream. */
const REREAD_MS = 4_000;

/** The file types the factory reads — the same list the wizard's upload accepts. */
const DOCUMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.txt,.md,.markdown,.csv";

const buildJobKey = (buildJobId: string) => ["build-job", buildJobId] as const;

export interface BuildingScreenProps {
  readonly buildJobId: string;
}

/** "45 s", "1 min 12 s" — how long the current step has been running. */
export function elapsedText(ms: number): string {
  const second = Math.max(0, Math.floor(ms / 1000));
  if (second < 60) return portalText("operations", "value_s_09263e7c", { v0: String(String(second)) });
  return portalText("operations", "value_min_value_s_7a6246d3", { v0: String(String(Math.floor(second / 60))), v1: String(String(second % 60)) });
}

/** Which of the five states the screen is in, from the row and whatever the stream has said since. */
export function phaseOf(row: BuildJobView | undefined, streamed: BuildPhase | null): BuildPhase {
  if (streamed === "succeeded" || streamed === "failed") return streamed;
  if (row === undefined) return streamed === null ? "queued" : streamed;
  if (row.state === "succeeded" || row.state === "failed") return row.state;
  if (row.waitingForDocument) return "waiting";
  if (row.state === "running" || streamed === "running") return "running";
  return "queued";
}

export function BuildingScreen({ buildJobId }: BuildingScreenProps): ReactNode {
  usePortalTranslation("operations");
  // A retry or another build starts a fresh subscription, clock and presentation queue.
  return <BuildingScreenBody key={buildJobId} buildJobId={buildJobId} />;
}

function BuildingScreenBody({ buildJobId }: BuildingScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [streamed, setStreamed] = useState<BuildPhase | null>(null);
  const [streamStep, setStreamStep] = useState<readonly StepRow[]>([]);
  const [streamProblem, setStreamProblem] = usePortalMessage();
  const [streamVoiceAgentId, setStreamVoiceAgentId] = useState<string | null>(null);
  const [lost, setLost] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [pending, setPending] = useState<"retry" | "replace" | null>(null);
  const [actionProblem, setActionProblem] = usePortalMessage();
  const picker = useRef<HTMLInputElement | null>(null);

  const build = useQuery({
    queryKey: buildJobKey(buildJobId),
    queryFn: () => gateway.getBuildJob(buildJobId),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === "succeeded" || state === "failed" ? false : REREAD_MS;
    },
  });

  useEffect(() => {
    const reread = (): void => {
      void queryClient.invalidateQueries({ queryKey: buildJobKey(buildJobId) });
    };
    const subscription = gateway.streamBuildJob(buildJobId, (frame) => {
      switch (frame.event) {
        case "build.step": {
          const data = readRecord(frame.data, "build.step");
          const row: StepRow = {
            ordinal: readWholeNumber(data["ordinal"], "build.step.ordinal"),
            label: readText(data["label"], "build.step.label"),
            state: "running",
            detail: null,
            startedAtMs: Date.now(),
          };
          setStreamed("running");
          setStreamStep((current) => (current.some((one) => one.ordinal === row.ordinal) ? current : [...current, row]));
          reread();
          break;
        }
        case "build.check":
        case "build.progress": {
          reread();
          break;
        }
        case "build.succeeded": {
          const data = readRecord(frame.data, "build.succeeded");
          setStreamVoiceAgentId(readText(data["voice_agent_id"], "build.succeeded.voice_agent_id"));
          setStreamed("succeeded");
          reread();
          // The agent is no longer `building`: every read of it — the list, the shell's count —
          // is out of date the moment this frame arrives.
          void queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
          break;
        }
        case "build.failed": {
          const data = readRecord(frame.data, "build.failed");
          setStreamProblem(() => (readText(data["message"], "build.failed.message")));
          setStreamed("failed");
          reread();
          void queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
          break;
        }
        case "resync": {
          // The stream admits it lost the thread; the row, read every few seconds, carries on.
          setLost(true);
          reread();
          break;
        }
        default:
          break;
      }
    });
    return () => {
      subscription.close();
    };
  }, [gateway, buildJobId, queryClient, setStreamProblem]);

  const row = build.data;
  const phase = phaseOf(row, streamed);
  /** Whether anything has said where the build is yet: the row, or a stream frame. */
  const known = row !== undefined || streamed !== null;
  // Which VoiceAgent: the row names it; before the row arrives, the wizard left it in the cache,
  // and the stream's `build.succeeded` names it too.
  const [originId] = useState(() => recallBuildOrigin(queryClient, buildJobId));
  const voiceAgentId = row !== undefined ? row.voiceAgentId : streamVoiceAgentId !== null ? streamVoiceAgentId : originId;
  // The prototype heads the screen "Roma orders · Delivery & ordering": the template's name is the
  // VoiceAgent's, read through the ordinary `getVoiceAgent`.
  const voiceAgent = useQuery({ ...voiceAgentQuery(gateway, voiceAgentId === null ? "none" : voiceAgentId), enabled: voiceAgentId !== null });

  // A clock for "1 min 12 s so far" — only while something is running.
  useEffect(() => {
    if (phase !== "running" && phase !== "queued") return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [phase]);

  const stepRow: readonly StepRow[] =
    row !== undefined && row.step.length > 0
      ? row.step.map((one) => ({
          ordinal: one.ordinal,
          label: one.label,
          state: one.state,
          detail: one.detail,
          startedAtMs: one.startedAtMs,
        }))
      : streamStep;
  const actualCurrent = stepRow.find((one) => one.state === "running" || one.state === "failed");
  const { ordinal: presentedOrdinal, pending: presentationPending } = useStagePresentation(stepRow, phase, streamed !== null);
  // One presentation phase drives the entire screen. The server's phase still drives reads.
  const displayedPhase = presentationPending ? "running" : phase;
  const presented = stepRow.find((one) => one.ordinal === presentedOrdinal);
  const current = presented ?? actualCurrent;
  const status = statusFor(displayedPhase);
  const active = displayedPhase === "running" || displayedPhase === "queued";
  const completed = stepRow.filter((one) => one.state === "succeeded" &&
    (!presentationPending || (presentedOrdinal !== null && one.ordinal < presentedOrdinal))).length;
  const problem =
    row !== undefined && row.failure !== null && row.failure.message !== null ? row.failure.message : streamProblem;
  const displayName = row !== undefined ? row.voiceAgentName : voiceAgent.data !== undefined ? voiceAgent.data.displayName : null;
  const agentTemplateName = voiceAgent.data === undefined ? null : voiceAgent.data.agentTemplateName;
  const firstAgentVersion = row === undefined ? true : !row.voiceAgentLive;
  const reachable = row !== undefined ? row.reachable : voiceAgent.data !== undefined && voiceAgent.data.reachable;

  const openBuild = async (next: string): Promise<void> => {
    if (voiceAgentId !== null) rememberBuildOrigin(queryClient, next, voiceAgentId);
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
    await navigate({ to: "/building/$buildJobId", params: { buildJobId: next } });
  };

  const retry = async (): Promise<void> => {
    if (voiceAgentId === null) return;
    setPending("retry");
    setActionProblem(null);
    try {
      await openBuild((await gateway.rebuildVoiceAgent(voiceAgentId, "reuse")).buildJobId);
    } catch (raised) {
      setActionProblem(() => (lifecycleRefusalOf(raised)));
    } finally {
      setPending(null);
    }
  };

  const replace = async (files: readonly File[]): Promise<void> => {
    if (voiceAgentId === null) return;
    setPending("replace");
    setActionProblem(null);
    try {
      if (phase !== "waiting") await gateway.rebuildVoiceAgent(voiceAgentId, "replace");
      for (const file of files) await gateway.uploadAgentDocument(voiceAgentId, file, () => undefined);
      await openBuild((await gateway.startVoiceAgentBuild(voiceAgentId)).buildJobId);
    } catch (raised) {
      setActionProblem(() => (lifecycleRefusalOf(raised)));
    } finally {
      setPending(null);
    }
  };

  return (
    <section className="c-screen">
      <p className="c-screen-kicker">
        {displayName === null || agentTemplateName === null ? portalText("operations", "build_value_6c87f393", { v0: String(buildJobId) }) : `${displayName} · ${agentTemplateName}`}
      </p>
      {/* Until the first read of the build (or a stream frame) says where it is, the heading says nothing: guessing
          "Tailoring…" flashed the wrong stage on a build that had already finished or was waiting for documents
          (owner, 2026-10-09). The space keeps the layout from jumping. */}
      <h1 className="c-building-title" aria-busy={known ? undefined : true}>
        {known ? headlineFor(displayedPhase, displayName) : "\u00a0"}
      </h1>
      <p className="c-building-dek">{known ? dekFor(displayedPhase, firstAgentVersion, reachable) : "\u00a0"}</p>

      <div className="c-building">
        <div>
          <div className="c-building-percent">
            <span className="c-building-status">{status}</span>
          </div>
          <div
            className="c-building-track"
            role="progressbar"
            aria-valuenow={displayedPhase === "succeeded" ? 100 : undefined}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={portalText("operations", "build_progress_c502e08d")}
            aria-valuetext={active ? (current?.label ?? "Waiting to start") : status}
          >
            {active ? (
              <div className="c-building-fill c-building-fill-active" />
            ) : displayedPhase === "succeeded" ? (
              <div className="c-building-fill" style={meterStyle(100)} />
            ) : null}
          </div>
          {stepRow.length > 0 ? (
            <p className="c-building-elapsed">{String(completed)} {portalText("operations", "of_2449d652")}{String(stepRow.length)} {portalText("operations", "stages_complete_98f83f5a")}</p>
          ) : null}
          {phase === "running" && actualCurrent !== undefined && actualCurrent.startedAtMs !== null ? (
            <p className="c-building-elapsed">
              {actualCurrent.label} · {elapsedText(now - actualCurrent.startedAtMs)} {portalText("operations", "so_far_e79c214d")}</p>
          ) : null}
          {presented?.state === "succeeded" && phase !== "failed" ? (
            <p className="c-building-elapsed" aria-live="polite">{portalText("operations", "completed_stage_c6cb7197")}{presented.label}.</p>
          ) : null}
          {phase === "running" && row?.stepCode === "read_document" ? (
            <p className="c-building-elapsed" role="status">
              {documentCheckText(row.documentCheck)}
            </p>
          ) : null}
        </div>

        <ol className="c-building-steps">
          {stepRow.length === 0 ? (
            <li className="c-building-step c-building-step-current">
              {phase === "waiting" ? portalText("operations", "waiting_for_the_documents_d817411b") : portalText("operations", "waiting_for_the_factory_to_pick_the_build_up_6f084e04")}
            </li>
          ) : (
            stepRow.map((one) => (
              <li
                key={one.ordinal}
                className={
                  one === current ? "c-building-step c-building-step-current" : "c-building-step"
                }
                data-state={presentationPending && (presentedOrdinal === null || one.ordinal >= presentedOrdinal) ? (one === current ? "running" : "queued") : one.state}
              >
                <span className="c-building-step-ordinal">{String(one.ordinal).padStart(2, "0")}</span>
                {one.label}
                {one.state === "succeeded" && one.detail !== null ? ` — ${one.detail}` : ""}
              </li>
            ))
          )}
        </ol>
      </div>

      {phase === "failed" ? (
        <p className="c-note c-note-critical">{problem === null ? portalText("operations", "wetalk_did_not_record_why_this_build_stopped_ccf0255e") : problem}</p>
      ) : null}
      {lost && phase !== "failed" && phase !== "succeeded" ? (
        <p className="c-note">{portalText("operations", "the_live_view_fell_behind_this_page_now_checks_the_build_e_36ce4569")}</p>
      ) : null}
      {build.isError && row === undefined ? (
        <p className="c-note c-note-critical">{portalText("operations", "this_build_could_not_be_read_3a08a8c4")}{apiErrorText(build.error, "")}).</p>
      ) : null}
      {actionProblem === null ? null : <p className="c-note c-note-critical">{actionProblem}</p>}

      <input
        ref={picker}
        type="file"
        multiple
        accept={DOCUMENT_ACCEPT}
        className="c-building-file"
        aria-label={portalText("operations", "choose_the_documents_to_build_from_620c3f21")}
        onChange={(event) => {
          const files = event.currentTarget.files === null ? [] : [...event.currentTarget.files];
          event.currentTarget.value = "";
          if (files.length > 0) void replace(files);
        }}
      />

      <div className="c-building-actions">
        {phase === "failed" && voiceAgentId !== null ? (
          <>
            <Button variant="primary" disabled={pending !== null} onClick={() => void retry()}>
              {pending === "retry" ? portalText("operations", "starting_e5f58095") : portalText("operations", "try_again_042c862e")}
            </Button>
            <Button variant="secondary" disabled={pending !== null} onClick={() => picker.current?.click()}>
              {pending === "replace" ? portalText("operations", "uploading_d921a79a") : portalText("operations", "replace_document_0b30132f")}
            </Button>
          </>
        ) : null}
        {phase === "waiting" && voiceAgentId !== null ? (
          <Button variant="primary" disabled={pending !== null} onClick={() => picker.current?.click()}>
            {pending === "replace" ? portalText("operations", "uploading_d921a79a") : portalText("operations", "upload_documents_6f8e7512")}
          </Button>
        ) : null}
        {voiceAgentId !== null ? (
          <Link
            to="/agents/$voiceAgentId"
            params={{ voiceAgentId }}
            search={{ tab: "live", range: "7d", outcome: "all", q: "", conversation: "" }}
            className={displayedPhase === "succeeded" ? "btn btn-primary" : "btn btn-secondary"}
          >
            {portalText("operations", "open_the_agent_66d53183")}</Link>
        ) : null}
        {displayedPhase === "succeeded" && voiceAgentId !== null ? (
          <Link
            to="/test-console"
            // glossary-lint-disable-next-line agent -- the ?agent= query parameter's own name (the UI-copy exemption)
            search={{ agent: voiceAgentId }}
            className="btn btn-secondary"
          >
            {portalText("operations", "test_it_0b448379")}</Link>
        ) : null}
        {displayedPhase === "succeeded" && voiceAgentId !== null && !reachable ? (
          <Link
            to="/agents/$voiceAgentId"
            params={{ voiceAgentId }}
            search={{ tab: "channels", range: "7d", outcome: "all", q: "", conversation: "" }}
            className="btn btn-secondary"
          >
            {portalText("operations", "connect_a_number_f563d967")}</Link>
        ) : null}
        {voiceAgentId === null ? (
          <Link to="/agents" search={{ status: "all", q: "" }} className="btn btn-secondary">
            {portalText("operations", "back_to_agents_c3c9c2a6")}</Link>
        ) : null}
      </div>
    </section>
  );
}

/** The prototype's `buildHeadline`, with the agent's name once it is known. */
function headlineFor(phase: BuildPhase, displayName: string | null): string {
  const subject = displayName ?? portalText("operations", "your_agent_8b1c816e");
  switch (phase) {
    case "succeeded":
      return portalText("operations", "value_is_ready_01d2684a", { v0: String(displayName ?? portalText("operations", "your_agent_007c6e54")) });
    case "failed":
      return portalText("operations", "the_build_stopped_4862c6b7");
    case "waiting":
      return portalText("operations", "value_is_waiting_for_its_documents_0b6d04f3", { v0: String(displayName ?? portalText("operations", "your_agent_007c6e54")) });
    default:
      return portalText("operations", "tailoring_value_to_your_business_c8915fed", { v0: String(subject) });
  }
}

function dekFor(phase: BuildPhase, firstAgentVersion: boolean, reachable: boolean): string {
  switch (phase) {
    case "failed":
      return firstAgentVersion
        ? portalText("operations", "nothing_was_published_and_nothing_was_charged_try_again_wi_3abe3fd6")
        : portalText("operations", "nothing_was_published_the_version_that_was_answering_keeps_2ad7a73f");
    case "succeeded":
      return reachable
        ? portalText("operations", "it_is_answering_open_it_to_follow_its_calls_or_test_it_9fb55094")
        : portalText("operations", "its_first_version_is_live_but_no_number_reaches_it_yet_so_9739ea8b");
    case "waiting":
      return portalText("operations", "upload_the_documents_to_build_from_the_build_starts_as_soo_a758e477");
    default:
      return portalText("operations", "you_can_close_this_page_and_carry_on_the_build_keeps_going_f31d4ef1");
  }
}

/** Only completed checks are reported. A response still in flight has no measurable percentage. */
function documentCheckText(check: DocumentCheckView | null): string {
  if (check === null) return portalText("operations", "processing_your_documents_and_checking_the_extracted_infor_d7fb2fda");
  if (check.outcome === "valid") return portalText("operations", "the_extracted_information_passed_check_value_b83a42aa", { v0: String(String(check.round)) });
  if (check.outcome === "invalid" || check.outcome === "no_draft") return portalText("operations", "refining_the_extracted_information_after_check_value_885497bd", { v0: String(String(check.round)) });
  return portalText("operations", "document_check_value_finished_waiting_for_the_build_result_71453821", { v0: String(String(check.round)) });
}

function statusFor(phase: BuildPhase): string {
  switch (phase) {
    case "succeeded": return portalText("operations", "complete_1f5a1abf");
    case "failed": return portalText("operations", "stopped_51e9111b");
    case "waiting": return portalText("operations", "waiting_for_documents_3cfbfc88");
    case "queued": return portalText("operations", "queued_6a599877");
    case "running": return portalText("operations", "in_progress_b6bd42e4");
  }
}
